import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';
import { teachableService } from '../services/teachableService';
import { PERMISSIONS } from '../config/permissions';
import type { User } from '../types/auth';
import type { StripeCustomerLink, DiscordProfile } from '../types/profile';
import {
  getNotificationHistory,
  clearNotificationHistory,
  type StoredNotification,
} from '../utils/notificationHistory';

type TabId = 'account' | 'billing' | 'discord' | 'notifications';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  {
    id: 'account',
    label: 'Account',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'discord',
    label: 'Discord',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
];

// Owned Teachable courses, rendered as its own card inside the Billing tab.
// Hidden entirely when the user has no enrolled courses to avoid showing an
// empty card to subscribers who aren't enrolled in any course.
const OwnedCoursesCard: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['teachable-courses'],
    queryFn: () => teachableService.getCourses(),
    staleTime: 60_000,
  });

  const enrolled = data?.enrolled_courses ?? [];
  if (enrolled.length === 0) return null;

  return (
    <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
      <h2 className="text-lg font-semibold text-gray-100 mb-4">Owned Courses</h2>
      <ul className="space-y-2">
        {enrolled.map((course) => (
          <li key={course.id} className="flex items-center justify-between gap-3">
            <a
              href={course.course_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-100 hover:text-accent-yellow transition-colors flex-1"
            >
              {course.name}
            </a>
            <span className="text-xs text-gray-400 tabular-nums">
              {course.percent_complete}% complete
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selfServiceEnabled = user?.feature_flags?.self_service_account === true;

  // Tab state from URL
  const activeTab = (searchParams.get('tab') as TabId) || 'account';
  const setActiveTab = (tab: TabId) => {
    const newParams = new URLSearchParams(searchParams);
    if (tab === 'account') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tab);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Edit states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    username: '',
    name: '',
    email: '',
  });

  // Password change
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });

  // Set password (for external auth users without a password)
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPasswordData, setNewPasswordData] = useState({
    new_password: '',
    new_password_confirm: '',
  });

  // Discord OAuth
  const [isConnectingDiscord, setIsConnectingDiscord] = useState(false);
  const [discordMessage, setDiscordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Handle Discord OAuth callback query params
  useEffect(() => {
    const discordSuccess = searchParams.get('discord_success');
    const discordError = searchParams.get('discord_error');

    if (discordSuccess === 'true') {
      setDiscordMessage({ type: 'success', text: 'Discord account connected successfully!' });
      queryClient.invalidateQueries({ queryKey: ['discordProfile'] });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('discord_success');
      newParams.set('tab', 'discord');
      setSearchParams(newParams, { replace: true });
    } else if (discordError) {
      const errorMessages: Record<string, string> = {
        access_denied: 'You cancelled the Discord authorization.',
        invalid_state: 'Security validation failed. Please try again.',
        guild_join_failed: 'Could not add you to the server. Please contact support.',
        guild_limit_reached: 'Your Discord account is in 100 servers (the maximum). Leave a server and try connecting again.',
        api_error: 'Discord API error. Please try again later.',
        user_not_found: 'Your session expired. Please log in and try again.',
        unexpected: 'An unexpected error occurred. Please try again.',
      };
      setDiscordMessage({
        type: 'error',
        text: errorMessages[discordError] || `Discord error: ${discordError}`,
      });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('discord_error');
      newParams.set('tab', 'discord');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Clear Discord message after 5 seconds
  useEffect(() => {
    if (discordMessage) {
      const timer = setTimeout(() => setDiscordMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [discordMessage]);

  // Handle Connect with Discord click
  const handleConnectDiscord = async () => {
    setIsConnectingDiscord(true);
    setDiscordMessage(null);
    try {
      const { authorize_url } = await profileService.getDiscordAuthUrl();
      window.location.href = authorize_url;
    } catch (error) {
      console.error('Failed to get Discord auth URL:', error);
      setDiscordMessage({ type: 'error', text: 'Failed to start Discord connection. Please try again.' });
      setIsConnectingDiscord(false);
    }
  };

  // Notification history
  const [notifications, setNotifications] = useState<StoredNotification[]>([]);

  // Load notification history
  useEffect(() => {
    setNotifications(getNotificationHistory());
  }, []);

  const handleClearNotifications = () => {
    if (confirm('Are you sure you want to clear your notification history?')) {
      clearNotificationHistory();
      setNotifications([]);
    }
  };

  // Fetch Stripe customer links
  const { data: stripeCustomers = [], isLoading: stripeLoading } = useQuery<StripeCustomerLink[]>({
    queryKey: ['stripeCustomers'],
    queryFn: async () => {
      const data = await profileService.getStripeCustomerLinks();
      console.log('Stripe customers data:', data);
      return data;
    },
  });

  // Fetch Discord profile
  const { data: discordProfile, isLoading: discordLoading } = useQuery<DiscordProfile | null>({
    queryKey: ['discordProfile'],
    queryFn: async () => {
      const data = await profileService.getDiscordProfile();
      console.log('Discord profile data:', data);
      return data;
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<Pick<User, 'username' | 'name' | 'email'>>) =>
      profileService.updateProfile(data),
    onSuccess: () => {
      refreshUser();
      setEditingField(null);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: profileService.changePassword,
    onSuccess: () => {
      setShowChangePassword(false);
      setPasswordData({ old_password: '', new_password: '', new_password_confirm: '' });
      alert('Password changed successfully. Please log in again.');
      window.location.href = '/login';
    },
  });

  // Set password mutation (for external auth users)
  const setPasswordMutation = useMutation({
    mutationFn: profileService.setPassword,
    onSuccess: () => {
      setShowSetPassword(false);
      setNewPasswordData({ new_password: '', new_password_confirm: '' });
      refreshUser();
      alert('Password set successfully! You can now log in with your username and password.');
    },
  });

  // Set primary customer mutation
  const setPrimaryMutation = useMutation({
    mutationFn: (linkId: number) => profileService.setPrimaryCustomer(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripeCustomers'] });
      refreshUser();
    },
  });

  // Billing portal mutation
  const billingPortalMutation = useMutation({
    mutationFn: (linkId: number) =>
      profileService.createBillingPortalSession(linkId, window.location.href),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  // Discord unlink mutation
  const unlinkDiscordMutation = useMutation({
    mutationFn: () => profileService.unlinkDiscord(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discordProfile'] });
      refreshUser();
    },
  });

  // Avatar
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const uploadAvatarMutation = useMutation({
    mutationFn: (file: File) => profileService.uploadAvatar(file),
    onSuccess: () => {
      refreshUser();
      setAvatarError(null);
    },
    onError: (error: any) => {
      setAvatarError(error.response?.data?.avatar?.[0] || 'Failed to upload avatar');
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationFn: () => profileService.deleteAvatar(),
    onSuccess: () => {
      refreshUser();
      setAvatarError(null);
    },
    onError: () => {
      setAvatarError('Failed to remove avatar');
    },
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setAvatarError('File must be under 2MB');
        return;
      }
      uploadAvatarMutation.mutate(file);
    }
    // Reset input so same file can be re-selected
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleEditField = (field: 'username' | 'name' | 'email') => {
    if (user) {
      setEditValues({ ...editValues, [field]: user[field] });
      setEditingField(field);
    }
  };

  const handleSaveField = (field: 'username' | 'name' | 'email') => {
    updateProfileMutation.mutate({ [field]: editValues[field] });
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getSubscriptionBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'bg-bull-blue';
      case 'past_due':
        return 'bg-accent-yellow text-dark-bg';
      case 'canceled':
        return 'bg-bear-red';
      default:
        return 'bg-gray-600';
    }
  };

  if (stripeLoading || discordLoading) {
    return (
      <div className="flex-1 min-h-0 bg-dark-bg flex flex-col">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-gray-400">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 bg-dark-bg flex flex-col">

      <main className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-100">Account Settings</h1>
          </div>

          <div className="flex gap-6">
            {/* Sidebar */}
            <nav className="w-48 flex-shrink-0">
              <div className="space-y-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded transition-colors ${
                      activeTab === tab.id
                        ? 'bg-dark-surface text-gray-100 border border-dark-border'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-dark-surface/50'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="border-t border-dark-border mt-3 pt-3">
                <Link
                  to="/courses"
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-dark-surface/50 rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Courses
                </Link>
              </div>
            </nav>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  {/* Avatar */}
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                    <h2 className="text-lg font-semibold text-gray-100 mb-4">Avatar</h2>
                    <div className="flex items-center gap-4">
                      {user?.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt="Avatar"
                          className="w-16 h-16 rounded-full object-cover border border-dark-border"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-dark-bg border border-dark-border flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => avatarInputRef.current?.click()}
                            disabled={uploadAvatarMutation.isPending}
                            className="px-3 py-1.5 bg-dark-bg hover:bg-dark-border text-gray-200 text-sm rounded border border-dark-border transition-colors disabled:opacity-50"
                          >
                            {uploadAvatarMutation.isPending ? 'Uploading...' : 'Change Avatar'}
                          </button>
                          {user?.avatar_url && (
                            <button
                              onClick={() => deleteAvatarMutation.mutate()}
                              disabled={deleteAvatarMutation.isPending}
                              className="px-3 py-1.5 text-bear-red hover:text-red-400 text-sm transition-colors disabled:opacity-50"
                            >
                              {deleteAvatarMutation.isPending ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Max 2MB. PNG, JPG, or GIF.</p>
                        {avatarError && (
                          <p className="text-xs text-bear-red mt-1">{avatarError}</p>
                        )}
                      </div>
                    </div>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </div>

                  {/* Account Information */}
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                    <h2 className="text-lg font-semibold text-gray-100 mb-4">Account Information</h2>

                    <div className="space-y-4">
                      {/* Username */}
                      <div className="flex items-center justify-between py-3 border-b border-dark-border">
                        <div className="flex-1">
                          <label className="text-sm text-gray-400">Username</label>
                          {editingField === 'username' ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                value={editValues.username}
                                onChange={(e) => setEditValues({ ...editValues, username: e.target.value })}
                                className="flex-1 px-3 py-1.5 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                              <button
                                onClick={() => handleSaveField('username')}
                                disabled={updateProfileMutation.isPending}
                                className="px-3 py-1.5 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="text-gray-100 mt-1">{user?.username}</div>
                          )}
                        </div>
                        {editingField !== 'username' && (
                          <button
                            onClick={() => handleEditField('username')}
                            className="text-sm text-bull-blue hover:text-green-400 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {/* Name */}
                      <div className="flex items-center justify-between py-3 border-b border-dark-border">
                        <div className="flex-1">
                          <label className="text-sm text-gray-400">Name</label>
                          {editingField === 'name' ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                value={editValues.name}
                                onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                className="flex-1 px-3 py-1.5 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                              <button
                                onClick={() => handleSaveField('name')}
                                disabled={updateProfileMutation.isPending}
                                className="px-3 py-1.5 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="text-gray-100 mt-1">{user?.name || 'Not set'}</div>
                          )}
                        </div>
                        {editingField !== 'name' && (
                          <button
                            onClick={() => handleEditField('name')}
                            className="text-sm text-bull-blue hover:text-green-400 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {/* Email */}
                      <div className="flex items-center justify-between py-3">
                        <div className="flex-1">
                          <label className="text-sm text-gray-400">Email</label>
                          {editingField === 'email' ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="email"
                                value={editValues.email}
                                onChange={(e) => setEditValues({ ...editValues, email: e.target.value })}
                                className="flex-1 px-3 py-1.5 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                              <button
                                onClick={() => handleSaveField('email')}
                                disabled={updateProfileMutation.isPending}
                                className="px-3 py-1.5 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="text-gray-100 mt-1">{user?.email}</div>
                          )}
                        </div>
                        {editingField !== 'email' && (
                          <button
                            onClick={() => handleEditField('email')}
                            className="text-sm text-bull-blue hover:text-green-400 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Password Section */}
                  {selfServiceEnabled ? (
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                    {user?.has_password === false ? (
                      <>
                        <h2 className="text-lg font-semibold text-gray-100 mb-2">Set Password</h2>
                        <p className="text-sm text-gray-400 mb-4">
                          Set a password to enable direct login with your username and password.
                        </p>

                        {!showSetPassword ? (
                          <button
                            onClick={() => setShowSetPassword(true)}
                            className="px-4 py-2 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                          >
                            Set Password
                          </button>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">New Password</label>
                              <input
                                type="password"
                                value={newPasswordData.new_password}
                                onChange={(e) =>
                                  setNewPasswordData({ ...newPasswordData, new_password: e.target.value })
                                }
                                className="w-full px-3 py-2 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
                              <input
                                type="password"
                                value={newPasswordData.new_password_confirm}
                                onChange={(e) =>
                                  setNewPasswordData({ ...newPasswordData, new_password_confirm: e.target.value })
                                }
                                className="w-full px-3 py-2 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setPasswordMutation.mutate(newPasswordData)}
                                disabled={setPasswordMutation.isPending}
                                className="px-4 py-2 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                              >
                                {setPasswordMutation.isPending ? 'Setting...' : 'Set Password'}
                              </button>
                              <button
                                onClick={() => {
                                  setShowSetPassword(false);
                                  setNewPasswordData({ new_password: '', new_password_confirm: '' });
                                }}
                                className="px-4 py-2 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                            {setPasswordMutation.isError && (
                              <p className="text-sm text-bear-red">
                                {(setPasswordMutation.error as any)?.response?.data?.new_password_confirm ||
                                  (setPasswordMutation.error as any)?.response?.data?.new_password?.[0] ||
                                  (setPasswordMutation.error as any)?.response?.data?.error ||
                                  'Failed to set password'}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <h2 className="text-lg font-semibold text-gray-100 mb-4">Change Password</h2>

                        {!showChangePassword ? (
                          <button
                            onClick={() => setShowChangePassword(true)}
                            className="px-4 py-2 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                          >
                            Change Password
                          </button>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Current Password</label>
                              <input
                                type="password"
                                value={passwordData.old_password}
                                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                                className="w-full px-3 py-2 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">New Password</label>
                              <input
                                type="password"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                className="w-full px-3 py-2 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Confirm New Password</label>
                              <input
                                type="password"
                                value={passwordData.new_password_confirm}
                                onChange={(e) =>
                                  setPasswordData({ ...passwordData, new_password_confirm: e.target.value })
                                }
                                className="w-full px-3 py-2 bg-dark-surface-light border border-dark-border rounded text-sm text-gray-100 focus:outline-none focus:border-bull-blue"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => changePasswordMutation.mutate(passwordData)}
                                disabled={changePasswordMutation.isPending}
                                className="px-4 py-2 bg-bull-blue hover:bg-green-600 text-white text-sm rounded transition-colors"
                              >
                                {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                              </button>
                              <button
                                onClick={() => {
                                  setShowChangePassword(false);
                                  setPasswordData({ old_password: '', new_password: '', new_password_confirm: '' });
                                }}
                                className="px-4 py-2 bg-dark-surface-light hover:bg-dark-border text-gray-300 text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                            {changePasswordMutation.isError && (
                              <p className="text-sm text-bear-red">
                                {(changePasswordMutation.error as any)?.message || 'Failed to change password'}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  ) : (
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6 opacity-50">
                    <h2 className="text-lg font-semibold text-gray-100 mb-2">Password</h2>
                    <p className="text-sm text-gray-400">Coming soon. Password management will be available shortly.</p>
                  </div>
                  )}
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div className="space-y-6">
                  {/* Subscription Status */}
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                    <h2 className="text-lg font-semibold text-gray-100 mb-4">Subscription Status</h2>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs text-white ${getSubscriptionBadgeColor(
                          user?.subscription_status || 'none'
                        )}`}
                      >
                        {user?.subscription_status || 'none'}
                      </span>
                      {user?.subscription_warning && (
                        <span className="text-xs text-accent-yellow">{user.subscription_warning}</span>
                      )}
                    </div>
                    {user?.stripe_metadata && user.stripe_metadata.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                          Active subscriptions
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {user.stripe_metadata.map((tier) => (
                            <span
                              key={tier}
                              className="px-2 py-0.5 rounded text-xs bg-dark-surface-light text-gray-200 border border-dark-border"
                            >
                              {PERMISSIONS[tier]?.displayName || tier}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Owned Courses (Teachable) */}
                  <OwnedCoursesCard />

                  {/* Stripe Billing Accounts */}
                  <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                    <h2 className="text-lg font-semibold text-gray-100 mb-4">Billing Accounts</h2>

                    {stripeCustomers.length === 0 ? (
                      <p className="text-sm text-gray-400">No Stripe accounts linked</p>
                    ) : (
                      <div className="space-y-3">
                        {stripeCustomers.map((link) => {
                          const activeProductNames = [
                            ...new Set(
                              (link.subscriptions ?? [])
                                .filter((s) => s.status === 'active' || s.status === 'trialing')
                                .map((s) => s.product_name)
                            ),
                          ];
                          const headline =
                            activeProductNames.length > 0
                              ? activeProductNames.join(', ')
                              : 'No active subscriptions';

                          return (
                            <div
                              key={link.id}
                              className="p-4 bg-dark-surface-light border border-dark-border rounded"
                            >
                              {/* Card Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-100">
                                      {headline}
                                    </span>
                                    {link.is_primary && (
                                      <span className="px-2 py-0.5 bg-bull-blue text-white text-xs rounded">
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 font-mono">
                                    {link.stripe_customer_id}
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  {!link.is_primary && (
                                    <button
                                      onClick={() => setPrimaryMutation.mutate(link.id)}
                                      disabled={setPrimaryMutation.isPending}
                                      className="text-xs px-3 py-1.5 bg-dark-surface hover:bg-dark-border text-gray-300 rounded transition-colors"
                                    >
                                      Set Primary
                                    </button>
                                  )}
                                  <button
                                    onClick={() => billingPortalMutation.mutate(link.id)}
                                    disabled={billingPortalMutation.isPending}
                                    className="text-xs px-3 py-1.5 bg-bull-blue hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50"
                                  >
                                    {billingPortalMutation.isPending ? 'Loading...' : 'Manage Billing'}
                                  </button>
                                </div>
                              </div>

                              {/* Subscriptions */}
                              {link.subscriptions && link.subscriptions.length > 0 && (
                                <div className="mb-3">
                                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                                    Subscriptions
                                  </div>
                                  <div className="space-y-1.5">
                                    {link.subscriptions.map((sub) => {
                                      const statusColor =
                                        sub.status === 'active' || sub.status === 'trialing'
                                          ? 'bg-bull-blue text-white'
                                          : sub.status === 'past_due'
                                            ? 'bg-accent-yellow text-dark-bg'
                                            : 'bg-gray-600 text-gray-300';
                                      const periodLabel =
                                        sub.status === 'active' || sub.status === 'trialing'
                                          ? 'Renews'
                                          : 'Ends';
                                      const periodDate = sub.period_end
                                        ? new Date(sub.period_end).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                          })
                                        : null;

                                      return (
                                        <div
                                          key={sub.id}
                                          className="flex items-center justify-between text-sm"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-gray-200 font-medium">
                                              {sub.product_name}
                                            </span>
                                            <span
                                              className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${statusColor}`}
                                            >
                                              {sub.status}
                                            </span>
                                          </div>
                                          {periodDate && (
                                            <span className="text-xs text-gray-400">
                                              {periodLabel} {periodDate}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Purchases */}
                              {link.checkouts && link.checkouts.length > 0 && (
                                <div className="mb-3">
                                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                                    Purchases
                                  </div>
                                  <div className="space-y-1.5">
                                    {link.checkouts.map((checkout) => {
                                      const paymentColor =
                                        checkout.payment_status === 'paid'
                                          ? 'bg-bull-blue text-white'
                                          : checkout.payment_status === 'unpaid'
                                            ? 'bg-accent-yellow text-dark-bg'
                                            : 'bg-gray-600 text-gray-300';

                                      return (
                                        <div
                                          key={checkout.id}
                                          className="flex items-center justify-between text-sm"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-gray-200 font-medium">
                                              {checkout.product_name}
                                            </span>
                                            <span
                                              className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${paymentColor}`}
                                            >
                                              {checkout.payment_status}
                                            </span>
                                          </div>
                                          <span className="text-xs text-gray-400">
                                            {checkout.formatted_amount}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Footer */}
                              <div className="text-xs text-gray-500 pt-2 border-t border-dark-border">
                                Last synced: {formatDate(link.last_synced_at)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Discord Tab */}
              {activeTab === 'discord' && (
                selfServiceEnabled ? (
                <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                  <h2 className="text-lg font-semibold text-gray-100 mb-4">Discord Integration</h2>

                  {discordMessage && (
                    <div
                      className={`mb-4 p-3 rounded text-sm ${
                        discordMessage.type === 'success'
                          ? 'bg-bull-blue/10 border border-bull-blue/30 text-bull-blue'
                          : 'bg-bear-red/10 border border-bear-red/30 text-bear-red'
                      }`}
                    >
                      {discordMessage.text}
                    </div>
                  )}

                  {!discordProfile ? (
                    <div>
                      <p className="text-sm text-gray-400 mb-4">
                        Connect your Discord account to automatically join the community server.
                      </p>
                      <button
                        onClick={handleConnectDiscord}
                        disabled={isConnectingDiscord}
                        className="px-4 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm rounded transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {isConnectingDiscord ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Connecting...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                            </svg>
                            Connect with Discord
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-3 bg-bull-blue/10 border border-bull-blue/30 rounded">
                        <p className="text-sm text-bull-blue flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Connected to the FZN Discord server
                        </p>
                      </div>

                      <div className="flex items-center justify-between py-3 border-b border-dark-border">
                        <div className="flex-1">
                          <label className="text-sm text-gray-400">Discord Account</label>
                          <div className="text-gray-100 mt-1 flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                            </svg>
                            {discordProfile.discord_id}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Linked {formatDate(discordProfile.linked_at)}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to unlink your Discord account? You will be removed from the server.')) {
                              unlinkDiscordMutation.mutate();
                            }
                          }}
                          disabled={unlinkDiscordMutation.isPending}
                          className="text-sm text-bear-red hover:text-red-400 transition-colors disabled:opacity-50"
                        >
                          {unlinkDiscordMutation.isPending ? 'Unlinking...' : 'Unlink'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                ) : (
                <div className="bg-dark-surface rounded-lg border border-dark-border p-6 opacity-50">
                  <h2 className="text-lg font-semibold text-gray-100 mb-2">Discord Integration</h2>
                  <p className="text-sm text-gray-400">Coming soon. Discord linking will be available shortly.</p>
                </div>
                )
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="bg-dark-surface rounded-lg border border-dark-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-100">Notification History</h2>
                    {notifications.length > 0 && (
                      <button
                        onClick={handleClearNotifications}
                        className="text-xs text-gray-400 hover:text-bear-red transition-colors"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-gray-400">No notifications yet. Enable notifications in widget settings to start tracking.</p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className="p-3 bg-dark-surface-light border border-dark-border rounded"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`px-1.5 py-0.5 text-[10px] rounded uppercase font-medium ${
                                      notification.source === 'wiseguy'
                                        ? 'bg-blue-500/20 text-blue-400'
                                        : 'bg-purple-500/20 text-purple-400'
                                    }`}
                                  >
                                    {notification.source}
                                  </span>
                                  <span className="text-sm text-gray-100 truncate">{notification.title}</span>
                                </div>
                                <p className="text-xs text-gray-400 truncate">{notification.body}</p>
                              </div>
                              <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                {formatDate(notification.timestamp)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
